import type { User } from "oidc-client-ts";
import {
  type Accessor,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import { env } from "~/env";
import { getUserManager } from "./oidc-client";

/**
 * On dev previews, the OIDC redirect_uri is pinned to the canonical
 * app.reliquo.dev domain (Keycloak has no support for per-subdomain
 * wildcard redirect URIs). Logins started on a preview subdomain must be
 * relayed through that canonical domain instead of calling signinRedirect
 * locally, since oidc-client-ts's state/session storage is origin-scoped.
 */
function needsRelay(): boolean {
  if (env.VITE_RELIQUO_ENV !== "dev") return false;
  const redirectHost = new URL(env.VITE_OIDC_REDIRECT_URI).hostname;
  return window.location.hostname !== redirectHost;
}

function relayTo(extraParams?: Record<string, string>) {
  const redirectHost = new URL(env.VITE_OIDC_REDIRECT_URI).hostname;
  const params = new URLSearchParams({
    returnHost: window.location.hostname,
    ...extraParams,
  });
  window.location.href = `https://${redirectHost}/auth/relay-login?${params.toString()}`;
}

export interface AuthContextValue {
  user: Accessor<User | null>;
  isLoading: Accessor<boolean>;
  isAuthenticated: Accessor<boolean>;
  accessToken: Accessor<string | null>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  register: () => void;
}

const AuthContext = createContext<AuthContextValue>();

export const AuthProvider: ParentComponent = (props) => {
  const [user, setUser] = createSignal<User | null>(null);
  const [isLoading, setIsLoading] = createSignal(true);

  createEffect(() => {
    const mgr = getUserManager();

    // Restore existing session from storage
    mgr.getUser().then((u) => {
      setUser(u);
      setIsLoading(false);
    });

    const onUserLoaded = (u: User) => {
      setUser(u);
    };
    const onUserUnloaded = () => {
      setUser(null);
    };
    const onUserSignedOut = () => {
      setUser(null);
    };

    mgr.events.addUserLoaded(onUserLoaded);
    mgr.events.addUserUnloaded(onUserUnloaded);
    mgr.events.addUserSignedOut(onUserSignedOut);

    onCleanup(() => {
      mgr.events.removeUserLoaded(onUserLoaded);
      mgr.events.removeUserUnloaded(onUserUnloaded);
      mgr.events.removeUserSignedOut(onUserSignedOut);
    });
  });

  const login = () => {
    if (needsRelay()) {
      relayTo();
      return Promise.resolve();
    }
    return getUserManager().signinRedirect();
  };

  const logout = () => getUserManager().signoutRedirect();

  const register = () => {
    if (needsRelay()) {
      relayTo({ prompt: "create", kc_action: "register" });
      return;
    }
    getUserManager().signinRedirect({
      extraQueryParams: {
        prompt: "create",
        kc_action: "register",
      },
    });
  };

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: () => {
      const u = user();
      return u !== null && !u.expired;
    },
    accessToken: () => user()?.access_token ?? null,
    login,
    logout,
    register,
  };

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
